import NamaCommon from "@/utils/common/NamaCommon.ts";
import LinkUtils from "@/utils/common/LinkUtils.ts";
import URLRequestParameters from "@/utils/constants/URLRequestParameters.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import DownloadPurpose from "@/utils/constants/DownloadPurpose.ts";
import Translator from "@/utils/common/Translator.ts";
import PrintingOption from "@/utils/constants/PrintingOption.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import {useErrorsStore, useMessagesStore} from "@/stores/ToastDataStore.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import NaMaText = namaTypes.NaMaText;
import LaunchReportRequest = namaTypes.LaunchReportRequest;
import PrintingParams = namaTypes.PrintingParams;

export default class NamaPrinting {
  static printList(entityType: string, ids: string[] | undefined | null, format: string, viewName: string | undefined | null, pageIndex: number, menuCode: string | undefined | null) {
    if (!ids || !ids.length)
      return;
    let url: string[] = [];
    url.push(this.createURL(entityType, StringUtils.toCSVLineWithSep(",", ids), null, format, viewName, false, pageIndex, "", menuCode));
    this.printNext(url, 0);
  }

  static createURL(entityType: string | undefined, id: string | undefined, tabTitle: namaTypes.NaMaText | undefined | null, format: string | undefined, viewName: string | undefined | null
    , form: boolean | undefined, pageIndex: number, reportId: string | undefined | null, menuCode: string | undefined | null) {
    let request: Record<string, any> = {};
    NamaCommon.addCommonParametersToRequest(request);
    let url = "runreport.rpt?" + LinkUtils.requestParam(PlaceTokens.PRM_OUTPUT_FORMAT, format);
    url += LinkUtils.requestParam(URLRequestParameters.DEPARTMENT, request.dep);
    url += LinkUtils.requestParam(URLRequestParameters.ANALYSIS_SET, request.an);
    url += LinkUtils.requestParam(URLRequestParameters.BRANCH, request.br);
    url += LinkUtils.requestParam(URLRequestParameters.SECTOR, request.sc);
    url += LinkUtils.requestParam(URLRequestParameters.LEGAL_ENTITY, request.leg);
    url += LinkUtils.requestParam(PlaceTokens.PRM_PRINTING_OPTION, this.getDirectPrintOption());
    if (ClientCache.getCustomGUISettings()?.openPrintInBrowserWindow)
      url += LinkUtils.requestParam(PlaceTokens.PRM_DOWNLOAD_PURPOSE, DownloadPurpose.PRINT);
    else
      url += LinkUtils.requestParam(PlaceTokens.PRM_DOWNLOAD_PURPOSE, DownloadPurpose.DOWNLOAD);
    url += LinkUtils.requestParam("timeStamp", ClientCache.currentTimeMillis());
    url += LinkUtils.requestParam(PlaceTokens.PRM_ENTITY_TYPE, entityType);
    url += LinkUtils.requestParam(PlaceTokens.PRM_ENTITYID, id);
    url += LinkUtils.requestParam(PlaceTokens.PRM_PAGE_ID, this.titleToParam(tabTitle, pageIndex));
    url += LinkUtils.requestParam(PlaceTokens.PRM_LANGUAGE, Translator.currentLang());
    url += LinkUtils.requestParam(PlaceTokens.PRM_VIEW, viewName);
    url += LinkUtils.requestParam(PlaceTokens.PRM_IS_FORM, form);
    url += LinkUtils.requestParam(PlaceTokens.PRM_FORM_REPORT_ID, reportId);
    if (ClientCache.getCustomGUISettings()?.openPrintInBrowserWindow)
      url += LinkUtils.requestParam("newP", "yesToNewPNama");
    url += LinkUtils.requestParam(PlaceTokens.PRM_MENU_HASH, menuCode);
    return url;
  }

  static titleToParam(tabTitle: namaTypes.NaMaText | undefined | null, pageIndex: number | undefined) {
    if (!tabTitle)
      return "";
    let params = "";
    let arabic = Translator.translateNaMaTextAr(tabTitle);
    let english = Translator.translateNaMaTextEn(tabTitle);
    if (tabTitle.resourceId)
      params += tabTitle.resourceId;
    if (arabic)
      params += ((params) ? "," : "") + arabic;
    if (english)
      params += ((params) ? "," : "") + english;
    if (ObjectChecker.isNotNullOrUndefined(pageIndex))
      params += ((params) ? "," : "") + ((pageIndex + 1) + ",");
    return params;
  }

  static getDirectPrintOption() {
    if (LoginUtils.loginInfo?.directPrint)
      return PrintingOption.DIRECT;
    else
      return PrintingOption.DIALOG;
  }

  static printNext(urls: string[] | undefined | null, startFrom: number) {
    if (!urls || startFrom >= urls.length)
      return;
    for (let i = startFrom; i < urls.length && i - startFrom < 10; i++) {
      let url = urls[i];
      if (url.includes("yesToNewPNama")) {
        this.openUrlInNewWindow(url);
      } else {
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = url;
        document.body.appendChild(iframe);
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 3000);
      }
    }
    if (startFrom + 10 >= urls.length)
      return;
    setTimeout(() => {
      this.printNext(urls, startFrom + 10);
    }, 500);
  }

  static openUrlInNewWindow(url: string) {
    window.open(url, "_blank", "");
  }

  static async printForm(entityType: string, id: string, entityCode: string, tabTitle: NaMaText, format: string, viewName: string, pageIndex: number, menuCode: string, fromPrintButton: boolean) {
    let request: LaunchReportRequest = {
      questions: "",
      reportId: "",
      outputFormat: format,
      printingOption: this.getDirectPrintOption(),
      downloadPurpose: DownloadPurpose.PRINT,
      entityType: entityType,
      entityId: id,
      entityCode,
      tabId: this.titleToParam(tabTitle, pageIndex),
      viewName: viewName,
      isForm: true,
      menuCode: menuCode
    };
    NamaCommon.addCommonParametersToRequest(request);
    const value = await NamaCommon.namaFetch(NamaURLS.IsPrintReportPossible, {data: request})
    if (value) {
      let response = await NamaCommon.namaFetch(NamaURLS.RunReport, {data: request}) as { list?: PrintingParams[] };
      await this.printNext_(tabTitle, pageIndex, request, fromPrintButton, response.list);
    }
    else
      useErrorsStore().pushErrorStr(Translator.translateId("couldNotFindFormToPrintRecord"));
  }

  static async printNext_(tabTitle: NaMaText, pageIndex: number, request: LaunchReportRequest, fromPrintButton: boolean, list?: PrintingParams[]) {
    if (ObjectChecker.isEmptyOrNull(list)) {
      useErrorsStore().pushErrorStr(Translator.translateId("couldNotFindPrintTempId"));
      return
    }
    if (fromPrintButton && ClientCache.shouldPrintUsingNamaServer()) { //&& !isMobileDevice()
      for (let p of list) {
          if (!ClientCache.getCustomGUISettings()?.doNotShowDocumentCodeInNamaServerPrintingLog) {
            p.entityCode = request.entityCode;
          }
          p.entityType = request.entityType;
          p.entityId = request.entityId;
        }
      await this.printUsingPrintingServer(list)
    } else {
      let urls: string[] = [];
      for (const printingParam of list) {
        let url = this.createURL(request.entityType, request.entityId, tabTitle, request.outputFormat, request.viewName, true, pageIndex, request.reportId, request.menuCode);
        url += LinkUtils.requestParam(PlaceTokens.PRM_REDIRECT, false);
        url += LinkUtils.requestParam(PlaceTokens.PRM_TEMP_PRINT_ID, printingParam.tempPrintId);
        urls.push(url);
      }
      this.printNext(urls, 0);
    }
  }

  static addDimensionsParametersToURL(url: string) {
    if (LoginUtils.loginInfo) {
      url += LinkUtils.requestParam(URLRequestParameters.DEPARTMENT, LoginUtils.loginInfo.dimensions?.department ? LoginUtils.loginInfo.dimensions?.department.id : '');
      url += LinkUtils.requestParam(URLRequestParameters.ANALYSIS_SET, LoginUtils.loginInfo.dimensions?.analysisSet ? LoginUtils.loginInfo.dimensions?.analysisSet.id : '');
      url += LinkUtils.requestParam(URLRequestParameters.BRANCH, LoginUtils.loginInfo.dimensions?.branch ? LoginUtils.loginInfo.dimensions?.branch.id : '');
      url += LinkUtils.requestParam(URLRequestParameters.SECTOR, LoginUtils.loginInfo.dimensions?.sector ? LoginUtils.loginInfo.dimensions?.sector.id : '');
      url += LinkUtils.requestParam(URLRequestParameters.LEGAL_ENTITY, LoginUtils.loginInfo.dimensions?.legalEntity ? LoginUtils.loginInfo.dimensions?.legalEntity.id : '');
    }
    return url;
  }

  static async printUsingPrintingServer(tempPrintIdsWithPrinterName: PrintingParams[]) {
    let body: Record<string, any> = {
      printParams: tempPrintIdsWithPrinterName,
      url: GUIUtilities.getServerAddressWithERP()
    };
    let port = 9494;
    let printURl = "http://localhost:" + port + "/namaPrint";
    try {
      let response = await NamaCommon.namaFetch(printURl, {data: body, doNotUseMainReqForHttp: true});
      console.log(response);
      if ("done" === response)
        useMessagesStore().pushMessageStr(Translator.translateId("Done"));
      else
        useErrorsStore().pushErrorStr(Translator.translateId("Error :  " + response));
    } catch (e) {
      useErrorsStore().pushErrorStr(Translator.translateId("There is no connection on port " + port
        + ", please make sure that the printing server is running. <br/>You can install the printing server from <a href='https://www.namasoft.com/bin/printing-server/printing-server/printing-server-launcher.exe' target='_blank'>here</a><br/>If you already have it please <a href='nama-printing-server://start'> run it fom here</a>"
        + "<br/>If you are using chrome make sure that the option <a target='_blank' href='chrome://flags/#block-insecure-private-network-requests'>chrome://flags/#block-insecure-private-network-requests</a> is disabled"));
    }
  }
}
